import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "addButton", "template", "limitMessage"]
  static values = {
    max: { type: Number, default: 99 },
    maxSimple: { type: Number, default: 3 },
    overLimitTpl: { type: String, default: "" }
  }

  connect() {
    this._updateAddButton()
    this._onTypeChanged = ({ detail: { type } }) => this._applyTypeLimit(type)
    document.addEventListener("campaign-type:changed", this._onTypeChanged)
  }

  disconnect() {
    document.removeEventListener("campaign-type:changed", this._onTypeChanged)
  }

  add() {
    if (this.visibleCount >= this.maxValue) return

    const index = Date.now()
    const html = this.templateTarget.innerHTML
      .replace(/__INDEX__/g, index)
      .replace(/__POSITION__/g, this.visibleCount)

    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    this.containerTarget.appendChild(wrapper.firstElementChild)

    this.renumber()
    this._updateAddButton()
    this._notifyCampaignFormChanged()
  }

  remove(event) {
    const step = event.currentTarget.closest("[data-step-row]")
    if (!step) return

    const destroyInput = step.querySelector("[data-destroy-field]")
    if (destroyInput) {
      destroyInput.value = "1"
      step.style.display = "none"
    } else {
      step.remove()
    }

    this.renumber()
    this._updateAddButton()
    this._notifyCampaignFormChanged()
  }

  get visibleCount() {
    return this.containerTarget.querySelectorAll("[data-step-row]:not([style*='display: none'])").length
  }

  renumber() {
    const visible = this.containerTarget.querySelectorAll("[data-step-row]:not([style*='display: none'])")
    visible.forEach((step, i) => {
      const badge = step.querySelector("[data-step-number]")
      if (badge) badge.textContent = i + 1
      const posInput = step.querySelector("[data-position-field]")
      if (posInput) posInput.value = i
    })
  }

  _applyTypeLimit(type) {
    this.maxValue = type === "simple" ? this.maxSimpleValue : 99
    this._updateAddButton()
  }

  _updateAddButton() {
    const count = this.visibleCount
    const max = this.maxValue
    const atMax = count >= max
    const overMax = count > max

    if (this.hasAddButtonTarget) {
      this.addButtonTarget.style.display = atMax ? "none" : ""
    }

    if (this.hasLimitMessageTarget) {
      const el = this.limitMessageTarget
      if (overMax) {
        el.style.display = "block"
        el.style.cssText += "; background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.25); color: #dc2626;"
        el.textContent = this._renderTpl(this.overLimitTplValue, count, max, count - max)
        el.dataset.overLimit = "true"
      } else if (atMax) {
        el.style.display = "block"
        el.style.cssText += "; background: rgba(217,119,6,0.07); border-color: rgba(217,119,6,0.2); color: #d97706;"
        el.textContent = el.dataset.atLimitText || el.textContent
        delete el.dataset.overLimit
      } else {
        el.style.display = "none"
        delete el.dataset.overLimit
      }
    }

    this._notifyCampaignFormChanged()
  }

  // Keep legacy name for compatibility with existing HAML target
  updateAddButton() { this._updateAddButton() }

  _renderTpl(tpl, count, max, excess) {
    if (!tpl) return `${count}/${max} — remove ${excess}`
    return tpl
      .replace("{{count}}", count)
      .replace("{{max}}", max)
      .replace("{{excess}}", excess)
  }

  _notifyCampaignFormChanged() {
    this.element.closest("form")?.dispatchEvent(new CustomEvent("campaign-form:changed", { bubbles: true }))
  }
}
